import { useState, useRef } from "react";
import { adminPostForm } from "lib/adminClient";
import { mediaUrl } from "lib/media";

/**
 * Two-upload picker for a slide: a wide desktop photo and an optional
 * portrait mobile photo (`image` / `mobileImage`). Shared by every slider
 * editor so the behaviour is identical on Homepage, Institutions and
 * Departments. `fallbackBg` paints the empty desktop tile (homepage slides
 * have a gradient of their own).
 */
/**
 * Recommended sizes per slider, from the shape each one actually renders at
 * (measured at a 1440x900 laptop and a 390x844 phone):
 *
 *   homepage hero      1435x676 desktop (2.12:1)   390x420 mobile (0.93:1)
 *   institution hero   1435x460 desktop (3.12:1)   390x340 mobile (1.15:1)
 *   department banner  1435x465 desktop (3.09:1)   same as institution
 *
 * Uploads are re-encoded to WebP at up to 1920px wide, so anything narrower
 * than the screen gets upscaled and looks soft — hence the 1920 widths.
 */
const PRESETS = {
  home: { desktop: "1920 × 900", mobile: "1080 × 1150" },
  institution: { desktop: "1920 × 620", mobile: "1080 × 940" },
  department: { desktop: "1920 × 620", mobile: "1080 × 940" },
};

const tilesFor = (variant) => {
  const p = PRESETS[variant] || PRESETS.institution;
  return [
    {
      key: "image",
      label: "Desktop / wide",
      hint: `Best ${p.desktop} · min 1400 wide`,
      cls: "h-[110px]",
    },
    {
      key: "mobileImage",
      label: "Mobile / portrait",
      hint: `Optional, under 768px · best ${p.mobile}`,
      cls: "h-[110px] max-w-[96px]",
    },
  ];
};

export default function SlideImages({ slide, onChange, fallbackBg, onError, variant = "institution" }) {
  const [busy, setBusy] = useState(null);
  const refs = useRef({});
  const tiles = tilesFor(variant);

  const upload = async (key, file) => {
    if (!file) return;
    setBusy(key);
    try {
      const form = new FormData();
      form.append("image", file);
      const { url } = await adminPostForm("/admin/media", form);
      onChange({ [key]: url });
    } catch (e) {
      onError?.(e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
    <div className="flex gap-3 items-start">
      {tiles.map(({ key, label, hint, cls }) => {
        const src = mediaUrl(slide?.[key]);
        return (
          <div key={key} className={key === "image" ? "flex-1 min-w-0" : "shrink-0"}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => refs.current[key]?.click()}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && refs.current[key]?.click()}
              className={`${cls} w-full rounded-lg overflow-hidden border border-line bg-[#eef0ee] flex items-center justify-center cursor-pointer relative`}
              style={{ background: src ? undefined : key === "image" ? fallbackBg : undefined }}
              title={`Upload ${label.toLowerCase()} photo`}
            >
              {src ? (
                <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <span className={`text-[11px] font-semibold text-center px-2 leading-tight ${fallbackBg && key === "image" ? "text-white/85" : "text-muted"}`}>
                  {key === "image" ? "Click to upload" : "＋ Mobile"}
                </span>
              )}
              {busy === key && (
                <span className="absolute inset-0 bg-black/40 text-white text-xs flex items-center justify-center">Uploading…</span>
              )}
            </div>
            <div className="mt-1 text-[11px] font-semibold text-ink leading-tight">{label}</div>
            <div className="text-[10.5px] text-muted leading-tight">{hint}</div>
            <div className="flex gap-2 mt-1">
              <button type="button" onClick={() => refs.current[key]?.click()} className="text-[12px] text-green font-semibold bg-transparent border-none cursor-pointer p-0">
                {src ? "Change" : "Upload"}
              </button>
              {src && (
                <button type="button" onClick={() => onChange({ [key]: null })} className="text-[12px] text-[#c44] bg-transparent border-none cursor-pointer p-0">
                  Remove
                </button>
              )}
            </div>
            <input
              ref={(el) => (refs.current[key] = el)}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                upload(key, e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </div>
        );
      })}
    </div>
    <div className="mt-1.5 text-[10.5px] text-muted leading-tight">
      The whole photo is shown by default, so nothing is cut off — the sizes
      above just avoid empty bands beside it. Saved at up to 1920px wide; a
      smaller file will look soft on a laptop.
    </div>
    </div>
  );
}
